<template>
  <div>
    <button class="btn btn-edit" @click="showModal">
      <img src="@/assets/images/admin/cashpayments/edit.png" alt="Edit" style="width: 25px; height: 25px;" />
    </button>

    <!-- Modal -->
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Contract</h5>
          <button type="button" class="btn-close" @click="closeModal">X</button>
        </div>
        <div class="modal-body">
          <div class="background-white">
            <!-- Tab Navigation -->
            <ul class="nav nav-tabs">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'general' }" @click="activeTab = 'general'">General</a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'history' }" @click="activeTab = 'history'">History</a>
              </li>
            </ul>

            <!-- General Tab -->
            <div v-if="activeTab === 'general'" class="tab-content">
              <div class="row align-items-start">
                <div class="col-md-5 col-12 d-flex">
                  <div class="card flex-grow-1">
                    <div class="card-body">
                      <h5>Contract details</h5>
                      <div class="row">
                        <div class="col-12 p-0">
                          <label for="tid" class="form-label">TID</label>
                          <input type="text" class="form-control" id="tid" v-model="form.tid" />
                          <div v-if="errors.tid" class="text-danger">{{ errors.tid }}</div>
                        </div>
                        <div class="col-md-6 col-12 p-0">
                          <label for="mid" class="form-label">MID</label>
                          <input type="text" class="form-control" id="mid" v-model="form.mid" />
                          <div v-if="errors.mid" class="text-danger">{{ errors.mid }}</div>
                        </div>
                        <div class="col-md-6 col-12 p-0">
                          <label for="serial-number" class="form-label">Serial number</label>
                          <input type="text" class="form-control" id="serial-number" v-model="form.serial_number" />
                          <div v-if="errors.serial_number" class="text-danger">{{ errors.serial_number }}</div>
                        </div>
                        <div class="col-12 p-0">
                          <label for="startDate" class="form-label">Start Date</label>
                          <input type="datetime" class="form-control" v-model="form.start_date" disabled />
                        </div>
                        <div class="col-12 mt-2 checkbox checkbox-success">
                          <input id="accessEnabled" type="checkbox" v-model="form.enabled" />
                          <label for="accessEnabled">Enabled</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-7 col-12 d-flex">
                  <div class="card flex-grow-1">
                    <div class="card-body">
                      <h5>Assignment</h5>
                      <div class="row">
                        <div class="col-md-4 col-12">
                          <label for="driverID" class="form-label">Driver ID</label>
                          <input type="text" class="form-control" id="driverID" v-model="form.driver_id"
                                 @input="clearDriverName" @keyup.enter="fetchDriverInfo"
                                 data-bs-toggle="tooltip" title="Please press Enter after entering ID" />
                        </div>
                        <div class="col-md-8 col-12">
                          <label for="name" class="form-label">Name</label>
                          <input type="text" class="form-control" id="name" v-model="form.name" disabled />
                        </div>
                        <!-- Fleet 區塊 -->
                        <div class="col-md-4 col-12"></div>
                        <div class="col-md-8 d-flex">
                          <div class="checkbox checkbox-success mt-4 d-flex align-items-center p-0">
                            <input id="subgroup-checkbox" type="checkbox" class="form-check-input" v-model="isFleetChecked" />
                            <label for="subgroup-checkbox" class="form-check-label">Fleet</label>
                          </div>
                          <div class="mt-4 ms-2 w-50">
                            <select id="subgroup-driver-id" class="form-select" v-model="form.fleet_id" :disabled="!isFleetChecked">
                              <option :value="null">Select...</option>
                              <option v-for="fleet in fleetStore.allFleets" :key="fleet.id" :value="fleet.id">
                                {{ fleet.name }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- History Tab -->
            <div v-if="activeTab === 'history'" class="tab-content">
              <div class="row">
                <div class="col-12">
                  <div class="table-responsive signal-table p-2">
                    <table class="table table-hover">
                      <thead>
                        <tr>
                          <th>Dated</th>
                          <th>Serial number</th>
                          <th>Enabled</th>
                          <th>Assignment type</th>
                          <th>Assigned to</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="history in histories" :key="history.id">
                          <td>{{ history.dated }}</td>
                          <td>{{ history.serial_number }}</td>
                          <td>
                            <i :class="history.enabled ? 'fa fa-check text-success' : 'fa fa-times text-danger'"></i>
                          </td>
                          <td>{{ history.assignment_type }}</td>
                          <td>{{ history.assignment_to }}</td>
                          <td>{{ history.description }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-green btn-ok me-2" @click="saveChanges">Save</button>
          <button class="btn btn-cancel" @click="closeModal">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, nextTick } from 'vue';
import { useTerminalsStore } from '@/store/admin/terminal';
import { useFleetStore } from '@/store/admin/fleet';
import { Tooltip } from 'bootstrap';
import Swal from 'sweetalert2';
import { useHttp } from '@/http/http'; 

// 接收傳入的 terminalId
const props = defineProps({
  terminalId: Number,
});

// 取得 Pinia store
const terminalStore = useTerminalsStore();
const fleetStore = useFleetStore();

// Modal 顯示狀態與 tab 切換
const show = ref(false);
const activeTab = ref('general');

// 定義表單資料，加入 fleet_id
const form = ref({
  id: '',
  tid: '',
  mid: '',
  serial_number: '',
  start_date: '',
  driver_id: '',
  enabled: false,
  name: '',
  fleet_id: null, // 預設為 null
});

// histories 的數據
const histories = ref([]);

// 錯誤訊息
const errors = ref({});

// 定義 fleet checkbox 狀態
const isFleetChecked = ref(false);

// 驗證表單
const validateForm = () => {
  errors.value = {};
  if (!form.value.tid) errors.value.tid = 'TID is required';
  if (!form.value.mid) errors.value.mid = 'MID is required';
  if (!form.value.serial_number) errors.value.serial_number = 'Serial number is required';
  return Object.keys(errors.value).length === 0;
};

// 根據 driver_id 查詢 driver 名稱
const fetchDriverInfo = async () => {
  try {
    const response = await useHttp().get(`api/admin/driver/info?driver_id=${form.value.driver_id}`);
    const driver = response.data.result;
    if (driver) {
      form.value.name = `${driver.first_name} ${driver.middle_name} ${driver.last_name}`;
    } else {
      form.value.name = 'Driver name is not found';
    }
  } catch (error) {
    form.value.name = 'Driver name is not found';
    console.error('Error fetching driver:', error);
  }
};

// 輸入 driver_id 時清空 driver 名稱
const clearDriverName = () => {
  form.value.name = '';
};

// 根據 terminalId 取得終端資料
const fetchTerminaData = async (terminalId) => {
  try {
    await terminalStore.getTerminalById(terminalId);
    setTerminalData(terminalStore.terminal);
    histories.value = terminalStore.terminal.histories;
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error fetching terminal data',
      text: error.message,
    });
  }
};

// 設定表單資料
const setTerminalData = (data) => {
  form.value.id = props.terminalId;
  form.value.tid = data.tid;
  form.value.mid = data.mid;
  form.value.serial_number = data.serial_number;
  form.value.start_date = data.start_date;
  form.value.driver_id = data.driver_id;
  form.value.enabled = data.enabled === 1;
  form.value.name = data.name;
  form.value.fleet_id = data.fleet?.id || null;
  if (data.fleet?.id) {
    isFleetChecked.value = true;
  }
};

// 顯示 modal 並載入資料
const showModal = async () => {
  show.value = true;
  if (props.terminalId) {
    await fetchTerminaData(props.terminalId);
    await fleetStore.fetchFleetForSelect();
    await nextTick();
    initializeTooltip();
  }
};

// 關閉 modal
const closeModal = () => {
  show.value = false;
};

// 儲存變更，並依 fleet checkbox 決定是否送出 fleet_id
const saveChanges = async () => {
  if (!validateForm()) {
    return;
  }
  // 準備 payload，並根據 isFleetChecked 決定 fleet_id
  const payload = { ...form.value, enabled: form.value.enabled ? 1 : 0 };
  if (!isFleetChecked.value) {
    payload.fleet_id = null;
  }
  try {
    const res = await terminalStore.updateTerminal(payload);
    if (res == false) {
      return;
    }
    closeModal();
  } catch (error) {
    console.error(error);
  }
};

// 初始化 Tooltip
const initializeTooltip = () => {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  tooltipTriggerList.forEach(tooltipTriggerEl => {
    new Tooltip(tooltipTriggerEl);
  });
};

onMounted(async () => {
  initializeTooltip();
});
</script>

<style scoped>
.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 203.83px;
  height: 46px;
  border-radius: 30px;
}
.btn-edit {
  color: #8476f5;
}
.btn-green:hover {
  background: #00b452;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 20px 0;
}
.modal-content {
  background: #e6e9ec;
  border-radius: 10px;
  width: 100%;
  max-width: 1200px;
  max-height: calc(100vh - 40px);
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: auto;
}
.background-white {
  background-color: white;
  padding: 10px;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.modal-body {
  border-radius: 10px;
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  min-height: 0;
}
.tab-content {
  display: flex;
  flex-direction: column;
  min-height: 500px;
}
.modal-body .row {
  display: flex;
  align-items: start;
}
.card {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.card-body {
  flex-grow: 1;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
  flex-shrink: 0;
}
label {
  margin-top: 5px;
}
.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}
.nav-link.active {
  background-color: #e6e9ec;
  color: black;
}
.card {
  background-color: #e6e9ec;
  margin-top: 10px;
  height: 400px;
}
.signal-table.table-responsive .table tbody tr td:nth-child(n+2) {
  min-width: 100px !important;
}
.btn-green:hover {
  background-color: #00b452;
}
.btn-ok {
  width: 150px;
}
.btn-cancel {
  background-color: #fff;
  color: #8476f4;
  border: 2px solid #8476f4;
  border-radius: 30px;
  width: 150px;
  height: 46px;
  padding: 14px 32px;
}
.btn-cancel:hover {
  background-color: #e6e6ff;
}

/* 手機版響應式設計 */
@media (max-width: 768px) {
  .modal-overlay {
    align-items: flex-start;
    padding: 10px;
  }
  
  .modal-content {
    max-height: calc(100vh - 20px);
    margin: 0;
    padding: 15px;
  }
  
  .modal-body {
    overflow-y: auto;
  }
  
  .modal-footer {
    position: sticky;
    bottom: 0;
    background: #e6e9ec;
    border-top: 1px solid #ddd;
    margin: 0 -15px -15px -15px;
    padding: 15px;
    flex-direction: column;
    gap: 10px;
  }
  
  .modal-footer .btn {
    width: 100%;
    margin: 0;
  }
  
  .card {
    height: auto;
    min-height: 250px;
  }
  
  .tab-content {
    min-height: auto;
  }
  
  .modal-body .row {
    flex-direction: column;
  }
  
  .col-md-5, .col-md-7 {
    margin-bottom: 15px;
  }
  
  .signal-table {
    font-size: 12px;
  }
  
  .signal-table th,
  .signal-table td {
    padding: 8px 4px;
  }
}
</style>
